import React, { useState } from 'react';
import * as User from 'data/User';
import styled from 'styled-components';
import { Main } from 'styles/themeColors';
import { useAuth } from 'lib/Auth';
import ConsignmentHistoryChange from 'data/ConsignmentHistoryChange';
import Divider from 'components/UI/Divider';

import FeatureFlag from 'data/FeatureFlag';
import ConsignmentModificationEntry from 'data/ConsignmentModificationEntry';
import { ConsignmentSnapshot, ConsignmentSnapshotEntry } from 'data/Consignment';
import { ALLOW_SAME_STATUS_ON_ORDER_HISTORY } from 'constants/consignmentStates';
import OrderHistoryItem from './OrderHistoryItem';

const Wrapper = styled.div`
   padding: 0 16px 16px 16px;
    `;

const UnderlinedButton = styled.button`
  background: none;
  border: none;
  color: ${Main.accent};
  text-decoration: underline;
  cursor: pointer;
  font-size: 1rem;
  padding: 0;
  margin: 0;

  &:hover {
    text-decoration: none;
  }
`;

interface OrderHistoryProps {
    consignmentHistoryChanges: ConsignmentHistoryChange[];
    consignmentState: string;
    featureFlags: FeatureFlag;
    consignmentModificationEntries?: ConsignmentModificationEntry[];
    consignmentSnapshotEntries?: ConsignmentSnapshotEntry[];
    consignmentSnapshot?: ConsignmentSnapshot;
    isSubscription?: boolean;
}

const OrderHistory: React.FC<OrderHistoryProps> = (props) => {

    const { user } = useAuth();
    const isVendor = User.isVendor(user!);
    let { consignmentHistoryChanges, consignmentState, featureFlags, isSubscription } = props;

    const [isExpanded, setIsExpanded] = useState(false);

    const toggleIsExpanded = () => {
        setIsExpanded(!isExpanded);
    };

    let mostRecentHistoryItem: ConsignmentHistoryChange | undefined;
    if (consignmentHistoryChanges[0]?.toConsignmentStatus === consignmentState
        && !ALLOW_SAME_STATUS_ON_ORDER_HISTORY.includes(consignmentHistoryChanges[0]?.toConsignmentStatus)) {
        // Remove the first element if it is the same as the current status. History shows past
        // states only. The allow-list exempts states whose row carries the sole record of an
        // action still in flight, which would otherwise be invisible for as long as it is open.
        consignmentHistoryChanges = consignmentHistoryChanges.filter((_, index) => index !== 0);

        if (consignmentHistoryChanges.length) mostRecentHistoryItem = consignmentHistoryChanges[0];
    } else {
        mostRecentHistoryItem = consignmentHistoryChanges[0];
    }

    if (!consignmentHistoryChanges.length) return null;


    return (
        <Wrapper>

            {!isExpanded && mostRecentHistoryItem && (
                <>
                    {/** Order progress section in collapsed state shows the most recent previous status of the order  */}
                    <OrderHistoryItem
                        item={mostRecentHistoryItem!}
                        isVendor={isVendor}
                        key={mostRecentHistoryItem?.id}
                        featureFlags={featureFlags}
                        consignmentModificationEntries={props.consignmentModificationEntries}
                        isSubscription={isSubscription}
                    />
                    {consignmentHistoryChanges.length > 1 && <UnderlinedButton onClick={toggleIsExpanded}>Expand</UnderlinedButton>}
                </>
            )}

            {isExpanded && consignmentHistoryChanges.map((item, index) => (
                <React.Fragment key={item.id}>
                    <OrderHistoryItem
                        item={item}
                        isVendor={isVendor}
                        featureFlags={featureFlags}
                        consignmentModificationEntries={props.consignmentModificationEntries}
                        isSubscription={isSubscription}
                    />
                    {index !== consignmentHistoryChanges.length - 1 && (
                        <Divider height="1px" bgColor={'#C1C9D1'} marginY='10px' noMarginX />
                    )}
                    {consignmentHistoryChanges.length > 1 && index === consignmentHistoryChanges.length - 1 && (
                        <UnderlinedButton onClick={toggleIsExpanded}>Collapse</UnderlinedButton>
                    )}
                </React.Fragment>
            ))}

            <div id='order-progress-printable-content' style={{display:'none'}}>
                    {consignmentHistoryChanges.map((item, index) => (
                        <React.Fragment key={index}>
                            <OrderHistoryItem
                                item={item}
                                isVendor={isVendor}
                                featureFlags={featureFlags}
                                consignmentModificationEntries={props.consignmentModificationEntries}
                                isSubscription={isSubscription}
                            />
                            {index !== consignmentHistoryChanges.length - 1 && (
                                <Divider height="1px" bgColor={'#C1C9D1'} marginY='10px' noMarginX />
                            )}
                        </React.Fragment>
                    ))}
            </div>
        </Wrapper>
    );
};

export default React.memo(OrderHistory);
